import type { AccessLevel, AccessOverlay } from './enums.js';
import { WORKING_ACCESS_LEVELS } from './enums.js';
import type { DepartmentId, ProjectId, UserId } from './ids.js';

export interface DepartmentAccess {
  readonly departmentId: DepartmentId;
  readonly slug: string;
  readonly level: AccessLevel;
}

export interface Principal {
  readonly id: UserId;
  readonly jobRoleSlug: string;
  readonly jobRoleId: string;
  readonly overlays: readonly AccessOverlay[];
  readonly departments: readonly DepartmentAccess[];
  readonly managedProjectIds: readonly ProjectId[];
}

export function isAdmin(principal: Principal): boolean {
  return principal.overlays.includes('admin');
}

export function isCeo(principal: Principal): boolean {
  return principal.overlays.includes('ceo');
}

export function isPrivileged(principal: Principal): boolean {
  return isAdmin(principal) || isCeo(principal);
}

export function departmentAccess(
  principal: Principal,
  slug: string,
): DepartmentAccess | undefined {
  return principal.departments.find((entry) => entry.slug === slug);
}

export function hasWorkingAccess(principal: Principal, slug: string): boolean {
  if (isPrivileged(principal)) return true;
  const access = departmentAccess(principal, slug);
  return access !== undefined && WORKING_ACCESS_LEVELS.includes(access.level);
}

export function canFileInto(principal: Principal, slug: string): boolean {
  if (isPrivileged(principal)) return true;
  return departmentAccess(principal, slug) !== undefined;
}

export function canBeAssigneeIn(principal: Principal, slug: string): boolean {
  return departmentAccess(principal, slug)?.level === 'full';
}

export function managesProject(principal: Principal, project: ProjectId): boolean {
  return principal.managedProjectIds.includes(project);
}
